import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';

// PRD PM §3.3 durum makinesi — bkz. backend PeriodStateMachine.
export const STATUS_VALUES = ['draft', 'active', 'evaluation', 'calibration', 'pending_close', 'closed', 'cancelled'];

export const STATUS_COLORS = {
  draft: { color: '#64748B', bg: '#F1F5F9' },
  active: { color: '#0291EF', bg: '#EFF6FF' },
  evaluation: { color: '#7C3AED', bg: '#F5F3FF' },
  calibration: { color: '#EA580C', bg: '#FFF7ED' },
  pending_close: { color: '#D97706', bg: '#FFFBEB' },
  closed: { color: '#16A34A', bg: '#DCFCE7' },
  cancelled: { color: '#E53935', bg: '#FEF2F2' },
};

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

const SystemBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  margin-right: 4px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'Nunito Sans', sans-serif;
  background: ${(p) => (p.$active ? 'rgba(2, 145, 239, 0.1)' : '#F1F5F9')};
  color: ${(p) => (p.$active ? '#0291EF' : '#C0C8D0')};
`;

// Dönem Motoru (Task 6) BE'sinin desteklediği uçlar: GET (liste/tekil), POST
// (sihirbaz), PUT {id} (Faz J, Task 2 — YALNIZ draft dönem düzenleme), POST
// {id}/activate|cancel|close — DELETE YOK.
const useEvaluationPeriodsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`/v1/evaluation-periods?${new URLSearchParams(params)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  // Sihirbazın son adımı "Başlat" seçildiğinde yaratılan dönemi hemen aktive
  // edebilmesi için (bkz. PeriodWizard) id döndürür — brief'teki temsili hook
  // yalnız boolean döndürüyordu, burada bilinçli genişletildi (id de "truthy").
  const create = useCallback(async (payload) => {
    try {
      const res = await api.post('/v1/evaluation-periods', payload);
      refresh();
      return res.data?.data || true;
    } catch {
      return null;
    }
  }, [refresh]);

  // Faz J (spec 2026-08-08-pm-hijyen-faz-j §3, J1, Task 2) — PUT {id}, YALNIZ
  // draft dönem. Payload PeriodWizard.buildPayload() ile AYNI şekil (create
  // yoluyla bit-eşit — bkz. PeriodWizard.js).
  const update = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/evaluation-periods/${id}`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // verb: activate|cancel|close|enable-disclosure (UI düzeltme demeti #69,
  // 2026-08-09 — kapalı dönemde geç tebliğ açma, tek yönlü)
  const action = useCallback(async (id, verb) => {
    try {
      await api.post(`/v1/evaluation-periods/${id}/${verb}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // Spec 2026-10-09 §5 — POST {id}/extend. Hata toast'ı interceptor'da; burada yalnız true/false
  // (GenericFormDrawer false'ta açık kalır).
  const extend = useCallback(async (id, payload) => {
    try {
      await api.post(`/v1/evaluation-periods/${id}/extend`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const statusOptions = useMemo(() => STATUS_VALUES.map((v) => ({
    id: v,
    name: t(`performance.period.status.${v}`),
  })), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('performance.period.name'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    dateFilterColumn('startDate', t('performance.period.startDate'), lang),
    dateFilterColumn('endDate', t('performance.period.endDate'), lang),
    // P1-C — planlanan sonuç ilan tarihi (opsiyonel; sihirbaz adım 1).
    dateFilterColumn('plannedAnnouncementDate', t('performance.period.plannedAnnouncementDate'), lang),
    {
      name: 'systemAEnabled',
      label: t('performance.period.systems'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return (
            <>
              <SystemBadge $active={row.systemAEnabled}>A</SystemBadge>
              <SystemBadge $active={row.systemBEnabled}>B</SystemBadge>
            </>
          );
        },
      },
    },
    {
      name: 'status',
      label: t('common.status'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        customBodyRender: (v) => {
          const cfg = STATUS_COLORS[v] || STATUS_COLORS.draft;
          return (
            <StatusBadge color={cfg.color} bg={cfg.bg}>
              {t(`performance.period.status.${v}`, { defaultValue: v })}
            </StatusBadge>
          );
        },
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('common.status')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => {
                  fl[i] = e.target.value ? [e.target.value] : [];
                  onChange(fl[i], i, col);
                }}
              >
                <MenuItem value=""><em>-</em></MenuItem>
                {statusOptions.map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    dateFilterColumn('createDate', t('performance.common.createDate'), lang),
  ], [t, lang, statusOptions, data]);

  return { fetching, data, pagination, columns, statusOptions, create, update, action, extend, refresh };
};

export default useEvaluationPeriodsApi;
